import { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import head from 'lodash/head';
import isNumber from 'lodash/isNumber';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getFilteredProducts } from '@yme/api';
import { usePaginationConfig } from '@yme/atoms/pagination';
import { VARIANTS_DRIVER_LICENSE_CATEGORY, QueryParams } from '@yme/common/constants';
import { useIsMount, useWindowSize } from '@yme/hooks/index';
import { getBloomreachModel, getIsInAuthor, getNavigationBreadCrumbs } from '@yme/selectors/common';
import { PixelPageView, setPixelPageView } from '@yme/utils/bloomreach';
import { DEFAULT_START_PAGE, INITIAL_PAGINATION_PARAM } from '../common/constants';
import { buildCounterParameters, buildPaginationData, buildRequestParam, getDefaultPerPageLimit, getFacetsValues, getInitialFacetsConfig, getParamsFromURL, getSelectedFacetsFromUrl, mergeFacetsCounts, parseDriverLicenceFilter, } from '../common/helpers';
import { useSortFilterOptions } from '../common/useSortFilterOptions';
import { MAX_RANGE_POINTER, MIN_RANGE_POINTER } from './constants';
import { setAvailableDriverLicenceToLocalStorage } from './helpers';
export function useComparisonFilter(props) {
    const { productEndpoint = '', categoryIds, filterFacetsConfig, perPageLimitSetting, sortOptionsType, productType, } = props;
    const history = useHistory();
    const isMount = useIsMount();
    const navigationBreadCrumbs = useSelector(getNavigationBreadCrumbs);
    const { productCatalog } = useSelector(getBloomreachModel);
    const isInAuthor = useSelector(getIsInAuthor);
    const facetsConfig = useMemo(() => getInitialFacetsConfig(filterFacetsConfig), [filterFacetsConfig]);
    const facetsId = useMemo(() => filterFacetsConfig.map((filterFacet) => filterFacet.id), [filterFacetsConfig]);
    const { pageParam, perPageParam, sortOrderParam, searchParam, facetsParams } = useMemo(() => getParamsFromURL(history.location.search, facetsId), [history.location.search, facetsId]);
    const { sortFilterOptions } = useSortFilterOptions({
        type: sortOptionsType,
    });
    const defaultSortOption = sortFilterOptions.find((option) => Boolean(option?.isValueDefault)) || sortFilterOptions[0];
    const sortOrderValue = sortOrderParam ?? defaultSortOption.value;
    const currentPageValue = Number(pageParam) || DEFAULT_START_PAGE;
    const perPageLimit = Number(perPageParam) || getDefaultPerPageLimit(window.innerWidth, perPageLimitSetting);
    const [data, setData] = useState([]);
    const [isDataExists, setIsDataExists] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [sortOrder, setSortOrder] = useState(sortOrderValue);
    const [isFilterCleared, setIsFilterCleared] = useState(true);
    const [facets, setFacets] = useState([]);
    const [isFacetsLoaded, setIsFacetsLoaded] = useState(false);
    const [paginationParam, setPaginationParam] = useState({
        ...INITIAL_PAGINATION_PARAM,
        currentPage: currentPageValue,
        activeElement: perPageLimit,
    });
    const [currentPage, setCurrentPage] = useState(currentPageValue);
    const [amountPerPage, setAmountPerPage] = useState(perPageLimit);
    const [search, setSearch] = useState(searchParam ?? '');
    const [counter, setCounter] = useState({
        from: currentPage,
        till: currentPage * amountPerPage,
        total: 0,
    });
    const { isMobileView } = useWindowSize();
    const { paginationConfig, activeItemValue: paginationActivePage, onClickItemHandler: onClickActivePageHandler, currentPerPageValue, setCurrentPerPageValue, setActiveItemValue, } = usePaginationConfig({
        paginationData: paginationParam,
        isMobileView,
    });
    const selectedFacets = useMemo(() => {
        if (facets.length) {
            return facets
                .filter((facet) => facet.values.some((value) => value.checked || (isNumber(value.rangeLow) && isNumber(value.rangeHigh))))
                .map((facet) => {
                if (facet.id.toLowerCase().includes(MIN_RANGE_POINTER)) {
                    return `${facet.id}:range (${facet.values[0].rangeLow} to *)`;
                }
                if (facet.id.toLowerCase().includes(MAX_RANGE_POINTER)) {
                    return `${facet.id}:range (* to ${facet.values[0].rangeHigh})`;
                }
                if (isNumber(facet.values[0].rangeLow) && isNumber(facet.values[0].rangeHigh)) {
                    return `${facet.id}:range (${facet.values[0].rangeLow} to ${facet.values[0].rangeHigh})`;
                }
                if (facet.id === VARIANTS_DRIVER_LICENSE_CATEGORY) {
                    const currentFacetValuesFiltered = facet.values.filter((value) => value.checked);
                    const currentFacetValues = currentFacetValuesFiltered.map((value) => parseDriverLicenceFilter(value.value));
                    return `${facet.id}: ${Array.from(new Set(currentFacetValues.flat())).join(',')}`;
                }
                return `${facet.id}: ${facet.values
                    .filter((value) => value.checked)
                    .map((value) => `"${value.value}"`)
                    .join(',')}`;
            })
                .join('|');
        }
        return getSelectedFacetsFromUrl(facetsParams);
    }, [facets, facetsParams]);
    const parameters = useMemo(() => {
        const param = buildRequestParam({
            categoryIds,
            facetsConfig,
            selectedFacets,
            sortOrder,
            amountPerPage,
            currentPage,
            search,
        });
        return param;
    }, [amountPerPage, categoryIds, currentPage, search, sortOrder, selectedFacets, facetsConfig]);
    useEffect(() => {
        if (facets.length) {
            setTimeout(() => {
                setAvailableDriverLicenceToLocalStorage(facets);
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facets.length, history.location.search]);
    // Initial request
    useEffect(() => {
        if (categoryIds.length && parameters) {
            setIsLoaded(false);
            (async () => {
                try {
                    setLoading(true);
                    const response = await getFilteredProducts({
                        endpoint: productEndpoint,
                        config: {
                            ...parameters,
                            selectedFacets: '',
                            search: '',
                            productType,
                        },
                    });
                    // filter-out facets that are not in the config
                    const facetsData = getFacetsValues(response.facets.filter((el) => {
                        return filterFacetsConfig.find((filterFacet) => filterFacet.id === el.id);
                    }), facetsParams, filterFacetsConfig);
                    setFacets(facetsData);
                    setIsFacetsLoaded(true);
                }
                catch (err) {
                    setLoading(false);
                }
            })();
        }
        else {
            setIsLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryIds]);
    // New request with updated parameters (get new data)
    useEffect(() => {
        if (categoryIds.length && parameters && isFacetsLoaded) {
            setIsLoaded(false);
            setCounter((prev) => ({
                ...prev,
                total: 0,
            }));
            (async () => {
                try {
                    setLoading(true);
                    const response = await getFilteredProducts({
                        endpoint: productEndpoint,
                        config: {
                            ...parameters,
                            productType,
                        },
                    });
                    setIsLoaded(true);
                    if (response) {
                        setData(response.results);
                        setIsDataExists(true);
                        setFacets((prevFacets) => mergeFacetsCounts(prevFacets, response.facets));
                        setCounter((prev) => ({
                            ...prev,
                            total: response.total,
                        }));
                    }
                    setLoading(false);
                }
                catch (err) {
                    setLoading(false);
                    setError(err);
                    setIsLoaded(true);
                }
            })();
        }
    }, [categoryIds.length, parameters, productEndpoint, isFacetsLoaded, productType, categoryIds]);
    useEffect(() => {
        if (categoryIds.length && parameters && isFacetsLoaded && !isInAuthor) {
            setPixelPageView(PixelPageView.CATEGORY, {
                cat_id: get(head(categoryIds), 'id', ''),
                cat: navigationBreadCrumbs.join('|'),
                domain_key: productCatalog,
            });
        }
    }, [categoryIds, parameters, navigationBreadCrumbs, isFacetsLoaded, productCatalog, isInAuthor]);
    useEffect(() => {
        if (paginationActivePage !== currentPage || counter.till !== amountPerPage) {
            setCurrentPage(paginationActivePage);
            const { fromValue, tillValue } = buildCounterParameters(paginationActivePage, amountPerPage);
            setCounter({
                ...counter,
                from: fromValue,
                till: tillValue,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationActivePage, amountPerPage]);
    useEffect(() => {
        const lengthData = data.length;
        const totalItems = counter.total;
        if (lengthData > 0 && totalItems > 0) {
            const paginationData = buildPaginationData(totalItems, amountPerPage);
            setPaginationParam({
                ...paginationData,
                currentPage: currentPageValue,
                activeElement: amountPerPage,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, counter, amountPerPage]);
    useEffect(() => {
        if (currentPerPageValue !== amountPerPage) {
            setAmountPerPage(currentPerPageValue);
            setActiveItemValue(DEFAULT_START_PAGE);
            setCurrentPage(DEFAULT_START_PAGE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPerPageValue]);
    useEffect(() => {
        if (isMount) {
            return;
        }
        if (Number(pageParam) !== paginationActivePage ||
            Number(perPageParam) !== currentPerPageValue) {
            const queryParams = new URLSearchParams(history.location.search);
            queryParams.set(QueryParams.PAGE, String(paginationActivePage));
            queryParams.set(QueryParams.PER_PAGE, String(currentPerPageValue));
            history.push({
                search: queryParams.toString(),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationActivePage, currentPerPageValue]);
    useEffect(() => {
        setFacets((prev) => getFacetsValues(prev, facetsParams));
        setSearch(searchParam ?? '');
        setPaginationParam((prev) => ({
            ...prev,
            currentPage: currentPageValue,
            activeElement: perPageLimit,
        }));
        setCurrentPage(currentPageValue);
        setAmountPerPage(perPageLimit);
        setSortOrder(sortOrderValue);
    }, [facetsParams, searchParam, currentPageValue, perPageLimit, sortOrderValue]);
    useEffect(() => {
        const hasFilters = Object.values(facetsParams).some(Boolean);
        if (isFilterCleared && hasFilters) {
            setIsFilterCleared(false);
        }
        if (!isFilterCleared && !hasFilters) {
            setIsFilterCleared(true);
        }
    }, [facetsParams, isFilterCleared]);
    const handleSortOrderChange = (value) => {
        if (value !== sortOrder) {
            const queryParams = new URLSearchParams(history.location.search);
            queryParams.set(QueryParams.SORT, value);
            history.push({
                search: queryParams.toString(),
            });
        }
    };
    const handleFilterCleared = () => {
        const queryParams = new URLSearchParams(history.location.search);
        facetsId.forEach((facetId) => {
            queryParams.delete(facetId);
        });
        history.push({
            search: queryParams.toString(),
        });
        setIsFilterCleared(true);
        setFacets((prev) => prev.map((facet) => ({
            ...facet,
            values: facet.values.map((value) => ({
                ...value,
                checked: false,
                rangeLow: undefined,
                rangeHigh: undefined,
            })),
        })));
    };
    const handleSearch = (word) => {
        const queryParams = new URLSearchParams(history.location.search);
        queryParams.set(QueryParams.SEARCH, word);
        history.push({
            search: queryParams.toString(),
        });
    };
    const setDefaultStartPage = () => {
        setActiveItemValue(DEFAULT_START_PAGE);
        setCurrentPage(DEFAULT_START_PAGE);
    };
    const handleUpdateFacets = (id, options) => {
        const currentFacet = facets.find((facet) => facet.id === id);
        if (currentFacet) {
            currentFacet.values = currentFacet.values.map((value) => ({
                ...value,
                checked: options.find((option) => option.value === value.value)?.checked ?? false,
            }));
            setDefaultStartPage();
            const queryParams = new URLSearchParams(history.location.search);
            if (currentFacet.values.some((value) => value.checked)) {
                queryParams.set(currentFacet.id, currentFacet.values
                    .filter((value) => value.checked)
                    .map((value) => value.value)
                    .join(','));
            }
            else {
                queryParams.delete(currentFacet.id);
            }
            history.push({
                search: queryParams.toString(),
            });
        }
    };
    const handleUpdateRangeFacet = (ids, rangeValues) => {
        setDefaultStartPage();
        const queryParams = new URLSearchParams(history.location.search);
        if (ids.length === 1) {
            queryParams.set(ids[0], `${rangeValues.low} to ${rangeValues.high}`);
        }
        else {
            queryParams.set(ids[0], `${rangeValues.low} to *`);
            queryParams.set(ids[1], `* to ${rangeValues.high}`);
        }
        history.push({
            search: queryParams.toString(),
        });
    };
    return {
        data,
        isDataExists,
        isLoaded,
        error,
        loading,
        // sorting order
        sortingProps: {
            sortOrder,
            sortFilterOptions,
            handleSortOrderChange,
        },
        // clear filter
        filterClearedProps: {
            isFilterCleared,
            handleFilterCleared,
        },
        // Pagination settings
        paginationProps: {
            paginationParam,
            paginationActivePage,
            onClickActivePageHandler,
            paginationConfig,
            currentPerPageValue,
            setCurrentPerPageValue,
        },
        // counter props
        counter,
        // search props
        searchInput: {
            searchInputValue: search,
            handleSearch,
        },
        facetsProps: {
            facets,
            handleUpdateFacets,
            handleUpdateRangeFacet,
        },
    };
}
